import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';
import {
    createDateTimeSchema,
    createZodFormResolver,
    LocalizedText,
    useAppClockService,
    Validation,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { withHookForm } from '@mews-commander/form';
import { FloatingPanel } from '@mews/b2b-ui';
import { PanelContentWrapper, PanelContentWrapperProps } from '../PanelContentWrapper';
import { AddProductsFormValues } from './AddProductsForm';
import { useAddProductsToReservation } from './utils/useAddProductsToReservation';
import { AddProductsToReservationPanelBody } from './AddProductsToReservationPanelBody';
import { AddProductsFormValueKey } from './types';

interface AddProductsToReservationPanelInnerProps extends Omit<PanelContentWrapperProps, 'children'> {
    opened: boolean;
    onClose: () => void;
    serviceId: string;
}

interface AddProductsToReservationPanelProps extends AddProductsToReservationPanelInnerProps {
    reservationId: string | undefined;
    defaultValues?: DefaultValues<AddProductsFormValues>;
}

const resolver = createZodFormResolver<AddProductsFormValues>(
    z
        .object({
            [AddProductsFormValueKey.productId]: Validation.String.Required,
            [AddProductsFormValueKey.consumedUtc]: Validation.DateTime.Utc,
            [AddProductsFormValueKey.quantity]: z.number().int(),
            [AddProductsFormValueKey.claimForToday]: z.boolean(),
        })
        .extend<{}>(createDateTimeSchema({ fieldName: AddProductsFormValueKey.consumedUtc, required: true }))
);

export const AddProductsToReservationPanel: React.FC<AddProductsToReservationPanelProps> = ({
    defaultValues,
    reservationId,
    onClose,
    ...other
}) => {
    const { loading, addProductsToReservation } = useAddProductsToReservation(reservationId, onClose);
    const appClockService = useAppClockService();

    return (
        <AddProductsToReservationPanelInner
            loading={loading}
            onSubmit={addProductsToReservation}
            onSubmitFail={() => {}}
            onClose={onClose}
            useFormProps={{
                resolver,
                defaultValues: {
                    consumedUtc: appClockService.getAppNowUtc().value,
                    claimForToday: true,
                    quantity: 1,
                    ...defaultValues,
                },
            }}
            {...other}
        />
    );
};

const AddProductsToReservationPanelInner = withHookForm<
    AddProductsFormValues,
    AddProductsToReservationPanelInnerProps & { loading: boolean }
>()(({ loading, opened, owner, resourceId, serviceId, onClose, submit }) => (
    <FloatingPanel
        loading={loading}
        active={opened}
        onClose={onClose}
        title={<LocalizedText textKey={TextKey.AddProductsToReservation} />}
        size="small"
        actions={[
            {
                variant: 'primary',
                children: <LocalizedText textKey={TextKey.AddProducts} />,
                onClick: submit,
                type: 'submit',
            },
        ]}
    >
        <PanelContentWrapper owner={owner} resourceId={resourceId}>
            <AddProductsToReservationPanelBody serviceId={serviceId} />
        </PanelContentWrapper>
    </FloatingPanel>
));
